<template>
  <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="state.isLoading"></div>
  <div class="container">
    <div class="custom-breadcrumb">
      <o-bread-crumb>
        <template v-slot:pageName>Policies</template>
      </o-bread-crumb>
    </div>

    <div class="row mt-4">
      <!-- Main Content Column -->
      <div class="col-lg-12 col-md-12">
        <!-- Tab Navigation -->
        <div class="theme-tabs mb-4">
            <button
                class="theme-tab"
                :class="{ 'theme-tab--active': activeTab === 'privacy' }"
                @click="activeTab = 'privacy'"
            >
                <i class="fas fa-shield-alt me-2"></i>
                Privacy Policy
            </button>
            <button
                class="theme-tab"
                :class="{ 'theme-tab--active': activeTab === 'terms' }"
                @click="activeTab = 'terms'"
            >
                <i class="fas fa-file-signature me-2"></i>
                Terms & Conditions
            </button>
        </div>

        <div v-show="activeTab === 'privacy'" class="card bg-white shadow-sm border-0 rounded-4 overflow-hidden mb-4">
          <div class="card-header bg-white border-0 pt-4 pb-0 px-4 d-flex justify-content-between align-items-center">
            <div>
              <h4 class="fw-bold text-dark mb-1">
                <i class="fas fa-shield-alt text-primary me-2"></i>Privacy Policy
              </h4>
              <p class="text-muted small mb-0">Manage your organization's privacy policy that will be displayed to users and members.</p>
            </div>
            <div class="header-actions">
              <button v-if="hasPrivacyPolicyChanges" type="button" class="btn-cancel" @click="cancelPrivacyPolicy" :disabled="!canWrite">Cancel</button>
              <button type="button" class="btn-save" @click="savePrivacyPolicy" :disabled="!canWrite || state.isLoading || !hasPrivacyPolicyChanges">
                <span v-if="state.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                <span v-else><i class="fas fa-check-circle me-2"></i> Save Policy</span>
              </button>
            </div>
          </div>
          
          <div class="card-body px-4 pb-4 pt-3">
            <div v-if="!canWrite" class="alert alert-info mb-4 border-0 rounded-3">
              <i class="fa fa-lock me-2"></i>
              You have read-only access to this module.
            </div>

            <o-form-group name="privacyPolicy">
              <OTextEditor
                v-model="privacyPolicy"
                placeholder="Write your privacy policy here..."
                :disabled="!canWrite"
              />
              <div v-if="validation.privacyPolicy">
                <div class="text-danger small mt-2" role="alert">
                  {{ validation.privacyPolicy[0] }}
                </div>
              </div>
            </o-form-group>

          </div>
        </div>

        <div v-show="activeTab === 'terms'" class="card bg-white shadow-sm border-0 rounded-4 overflow-hidden mb-5">
          <div class="card-header bg-white border-0 pt-4 pb-0 px-4 d-flex justify-content-between align-items-center">
            <div>
              <h4 class="fw-bold text-dark mb-1">
                <i class="fas fa-file-signature text-primary me-2"></i>Terms & Conditions
              </h4>
              <p class="text-muted small mb-0">Manage your organization's terms and conditions that will be displayed to users and members.</p>
            </div>
            <div class="header-actions">
              <button v-if="hasTermsConditionsChanges" type="button" class="btn-cancel" @click="cancelTermsConditions" :disabled="!canWrite">Cancel</button>
              <button type="button" class="btn-save" @click="saveTermsConditions" :disabled="!canWrite || state.isLoading || !hasTermsConditionsChanges">
                <span v-if="state.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                <span v-else><i class="fas fa-check-circle me-2"></i> Save Terms</span>
              </button>
            </div>
          </div>
          
          <div class="card-body px-4 pb-4 pt-3">
            <div v-if="!canWrite" class="alert alert-info mb-4 border-0 rounded-3">
              <i class="fa fa-lock me-2"></i>
              You have read-only access to this module.
            </div>

            <o-form-group name="termsConditions">
              <OTextEditor
                v-model="termsConditions"
                placeholder="Write your terms & conditions here..."
                :disabled="!canWrite"
              />
              <div v-if="validation.termsConditions">
                <div class="text-danger small mt-2" role="alert">
                  {{ validation.termsConditions[0] }}
                </div>
              </div>
            </o-form-group>

          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Toast Notification -->
  <ToastNotification v-model="showToast" :type="toastType" :title="toastTitle" :message="toastMessage" :duration="3000" position="top-right" />
</template>

<script>
import { OFormGroup, OBreadCrumb } from '@/components/OmnihControl';
import OTextEditor from '@/components/OmnihControl/OTextEditor.vue';
import ToastNotification from '@/components/member/ToastNotification.vue';
import { reactive, ref, onMounted, watch, computed } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'Policies',
  components: {
    OBreadCrumb,
    OFormGroup,
    OTextEditor,
    ToastNotification
  },
  setup() {
    const store = useStore();
    
    const selfLoaded = ref(store.state.Auth.selfLoaded);
    const activeTab = ref('privacy');
    
    const showToast = ref(false);
    const toastTitle = ref('');
    const toastMessage = ref('');
    const toastType = ref('success');

    const triggerToast = (title, message, type = 'success') => {
      toastTitle.value = title;
      toastMessage.value = message;
      toastType.value = type;
      showToast.value = true;
    };
    
    const privacyPolicy = ref('');
    const termsConditions = ref('');
    
    const state = reactive({
      isLoading: false,
      selectedBusinessProfile: {
        corporate_id: null,
        branch_id: null,
        merchant_id: null
      }
    });
    
    const validation = reactive({});

    const canWrite = computed(() => {
      const userLevel = store.state.Auth.currUser?.level;
      
      if (userLevel === 'root') {
        return true;
      }

      const userModules = store.state.Auth.userInfo?.modules || [];
      const policiesModule = userModules.find(mod => mod.module_name === 'policies');
      
      return policiesModule && policiesModule.write === 1;
    });

    // Simple validation function
    const validateContent = (content, fieldName) => {
      const errors = [];
      
      if (!content || content.trim().length === 0) {
        errors.push(`${fieldName} is required`);
      }
      
      if (content && content.length > 50000) {
        errors.push(`${fieldName} must not exceed 50,000 characters`);
      }
      
      return {
        isValid: errors.length === 0,
        errors: errors,
        sanitized: content ? content.trim() : ''
      };
    };

    watch(
      [() => store.state.Auth.selfLoaded, () => store.state.Auth.userInfo.modules],
      async ([selfLoadedVal, modules]) => {
        if (selfLoadedVal && modules && Array.isArray(modules)) {
          selfLoaded.value = selfLoadedVal;

          const businessProfile = await store.dispatch('Auth/getBusinessProfile');
          state.selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
          state.selectedBusinessProfile.branch_id = businessProfile.branch_id;
          state.selectedBusinessProfile.merchant_id = businessProfile.merchant_id;

          loadPolicies();
        }

        const level = store.state.Auth.currUser.level;

        if (level === 'root') {
          state.write = true;
          state.isRoot = true;
        }
      },
      { immediate: true }
    );

    onMounted(async () => {
      try {
        state.isLoading = true;
        const businessProfile = await store.dispatch('Auth/getBusinessProfile');
        state.selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
        state.selectedBusinessProfile.branch_id = businessProfile.branch_id;
        state.selectedBusinessProfile.merchant_id = businessProfile.merchant_id;

        loadPolicies();
      } catch (err) {
        console.error(err);
        validation.error = 'Failed to load business profile';
      } finally {
        state.isLoading = false;
      }
    });


    const loadPolicies = async () => {
      try {
        state.isLoading = true;

        try {
          await store.dispatch('Policies/get_privacy_policy');
          const rawPrivacyPolicy = store.getters['Policies/privacyPolicy'];
          privacyPolicy.value = rawPrivacyPolicy || '';
        } catch (error) {
          console.warn('Privacy Policy not found or error:', error);
          privacyPolicy.value = '';
        }

        try {
          await store.dispatch('Policies/get_terms_conditions');
          const rawTermsConditions = store.getters['Policies/termsConditions'];
          termsConditions.value = rawTermsConditions || '';
        } catch (error) {
          console.warn('Terms & Conditions not found or error:', error);
          termsConditions.value = '';
        }
        
      } catch (error) {
        console.error('Error loading policies:', error);
        triggerToast('Error', 'Failed to load policies', 'error');
      } finally {
        state.isLoading = false;
      }
    };


    const savePrivacyPolicy = async () => {
      try {
        state.isLoading = true;

        const trimmedContent = privacyPolicy.value.trim();
        
        if (trimmedContent.length === 0) {
          const existingPolicy = store.getters['Policies/privacyPolicy'];

          if (existingPolicy) {
            await store.dispatch('Policies/delete_privacy_policy');
            privacyPolicy.value = '';
            triggerToast('Success', 'Privacy Policy deleted successfully!', 'success');
          } else {
            triggerToast('Info', 'No Privacy Policy to delete.', 'info');
          }
          return;
        }

        const result = validateContent(trimmedContent, 'Privacy Policy');
        
        if (!result.isValid) {
          validation.privacyPolicy = result.errors;
          triggerToast('Error', 'Please fix validation errors before saving', 'error');
          return;
        }

        const existingPolicy = store.getters['Policies/privacyPolicy'];
        
        if (existingPolicy) {
          await store.dispatch('Policies/update_privacy_policy', {
            content: result.sanitized
          });
        } else {
          await store.dispatch('Policies/save_privacy_policy', {
            content: result.sanitized
          });
        }
        
        privacyPolicy.value = result.sanitized;
        triggerToast('Success', 'Privacy Policy saved successfully!', 'success');
        Object.keys(validation).forEach(key => delete validation[key]);
        
      } catch (err) {
        console.error('Error saving privacy policy:', err);
        const errorMessage = err.response?.data?.message || 'Failed to save Privacy Policy';
        triggerToast('Error', errorMessage, 'error');
        validation.privacyPolicy = [errorMessage];
      } finally {
        state.isLoading = false;
      }
    };


    const saveTermsConditions = async () => {
      try {
        state.isLoading = true;

        const trimmedContent = termsConditions.value.trim();
        
        if (trimmedContent.length === 0) {
          const existingTerms = store.getters['Policies/termsConditions'];

          if (existingTerms) {
            await store.dispatch('Policies/delete_terms_conditions');
            termsConditions.value = '';
            triggerToast('Success', 'Terms & Conditions deleted successfully!', 'success');
          } else {
            triggerToast('Info', 'No Terms & Conditions to delete.', 'info');
          }
          return;
        }

        const result = validateContent(trimmedContent, 'Terms & Conditions');
        
        if (!result.isValid) {
          validation.termsConditions = result.errors;
          triggerToast('Error', 'Please fix validation errors before saving', 'error');
          return;
        }

        const existingTerms = store.getters['Policies/termsConditions'];
        
        if (existingTerms) {
          await store.dispatch('Policies/update_terms_conditions', {
            content: result.sanitized
          });
        } else {
          await store.dispatch('Policies/save_terms_conditions', {
            content: result.sanitized
          });
        }
        
        termsConditions.value = result.sanitized;
        triggerToast('Success', 'Terms & Conditions saved successfully!', 'success');
        Object.keys(validation).forEach(key => delete validation[key]);
        
      } catch (err) {
        console.error('Error saving terms conditions:', err);
        const errorMessage = err.response?.data?.message || 'Failed to save Terms & Conditions';
        triggerToast('Error', errorMessage, 'error');
        validation.termsConditions = [errorMessage];
      } finally {
        state.isLoading = false;
      }
    };

    const cancelPrivacyPolicy = () => {
      const rawPrivacyPolicy = store.getters['Policies/privacyPolicy'];
      privacyPolicy.value = rawPrivacyPolicy || '';
      validation.privacyPolicy = null;
      triggerToast('Info', 'Changes reverted', 'info');
    };

    const cancelTermsConditions = () => {
      const rawTermsConditions = store.getters['Policies/termsConditions'];
      termsConditions.value = rawTermsConditions || '';
      validation.termsConditions = null;
      triggerToast('Info', 'Changes reverted', 'info');
    };

    const hasPrivacyPolicyChanges = computed(() => {
      const rawPrivacyPolicy = store.getters['Policies/privacyPolicy'] || '';
      return privacyPolicy.value !== rawPrivacyPolicy;
    });

    const hasTermsConditionsChanges = computed(() => {
      const rawTermsConditions = store.getters['Policies/termsConditions'] || '';
      return termsConditions.value !== rawTermsConditions;
    });

    return {
      state,
      activeTab,
      privacyPolicy,
      termsConditions,
      validation,
      showToast,
      toastTitle,
      toastMessage,
      toastType,
      canWrite,
      hasPrivacyPolicyChanges,
      hasTermsConditionsChanges,
      savePrivacyPolicy,
      saveTermsConditions,
      cancelPrivacyPolicy,
      cancelTermsConditions
    };
  }
};
</script>

<style scoped>
.theme-tabs {
    display: flex;
    gap: 4px;
    background: #F5F6FA;
    padding: 4px;
    border-radius: 12px;
    width: fit-content;
}

.theme-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #717784;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.theme-tab:hover {
    color: #737f96;
}

.theme-tab--active {
    background: #FFFFFF;
    color: var(--primary-color, #F93E3E);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    font-weight: 600;
}

.policy-card {
  width: 100%;
  max-width: 800px;
  margin-left: 0;
  margin-right: auto;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: border-radius 0.3s ease;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

.btn-cancel {
    padding: 8px 20px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 8px 20px;
    background: var(--primary-color, #F93E3E);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

@media (max-width: 768px) {
  .policy-card {
    max-width: 100%; 
    margin-left: 10px;
    margin-right: 10px;
  }
  
  .card-header {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 12px;
  }
  
  .header-actions {
      width: 100%;
      justify-content: flex-start;
  }
}

/* Override rich text editor height to ensure good UX */
:deep(.o-text-editor__content) {
  min-height: 250px;
  max-height: 75vh;
  overflow-y: auto;
}

.section-description {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.5);
}

.section-description h5 {
  color: #333;
  font-size: 1.2rem;
}


/* Keyboard Shortcuts Styling */
.shortcuts-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  top: 20px;
  z-index: 1;
}

.shortcuts-list {
  font-size: 0.9rem;
}

.shortcut-group {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.75rem;
}

.shortcut-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.shortcut-item {
  padding: 0.25rem 0;
  font-size: 0.85rem;
}

.shortcut-action {
  color: #555;
  font-weight: 500;
}

.kbd-shortcut {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  color: #495057;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.shortcuts-card h6 {
  font-size: 0.9rem;
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .shortcuts-card {
    position: static !important;
    margin-top: 1.5rem;
  }
}

@media (max-width: 767.98px) {
  .shortcut-item {
    font-size: 0.8rem;
  }
  
  .kbd-shortcut {
    font-size: 0.7rem;
    padding: 0.15rem 0.3rem;
  }
}
</style>